import { z } from 'zod';

/**
 * The client PWA's own response shapes: the routes only it calls.
 *
 * A slot, a booking and the policy come from `features/scheduling`'s **barrel**, which
 * BP-00 R5 allows. Re-deriving those here would mean two definitions of one contract,
 * and the second would be the one that rots.
 *
 * The menu, the add-ons and the technicians do **not** come from the catalogue and
 * scheduling feature apis, because those routes need a staff session and §6 puts the
 * OTP step *after* the basket and the slot. They come from BP-04's `/public/*` routes,
 * which answer a visitor with exactly what a booking page prints — no bill of
 * materials, no GST internals, no `user_id`.
 */

/** One bookable service (`GET /public/services`). */
export const publicServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  duration_min: z.number(),
  price_paise: z.number(),
  is_active: z.boolean(),
});

/** One bookable add-on; `service_id` is null for a global one. */
export const publicAddonSchema = z.object({
  id: z.string(),
  service_id: z.string().nullable(),
  name: z.string(),
  duration_min: z.number(),
  price_paise: z.number(),
  is_active: z.boolean(),
});

/** One technician taking online bookings (`GET /public/staff`). */
export const publicStaffSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  colour: z.string(),
  is_bookable: z.boolean(),
  active: z.boolean(),
});

/** `POST /auth/client/otp/request` → `{resend_after_s}` (§5, R14). */
export const otpRequestSchema = z.object({ resend_after_s: z.number() });

/** `POST /auth/client/otp/verify` → `{client_id}`; the session itself is a cookie. */
export const otpVerifySchema = z.object({ client_id: z.string() });

/**
 * `GET /public/bookings/{ref}?token=` (§5).
 *
 * Deliberately smaller than `BookingOut`: a link that leaks shows an appointment, not a
 * client record — no `client_id`, no notes, no policy beyond the cut-off the client needs
 * in order to act.
 */
export const publicBookingSchema = z.object({
  ref: z.string(),
  status: z.string(),
  start_at: z.string(),
  end_at: z.string(),
  client_cutoff_hours: z.number(),
  may_change: z.boolean(),
});

export type PublicService = z.infer<typeof publicServiceSchema>;
export type PublicAddon = z.infer<typeof publicAddonSchema>;
export type PublicStaff = z.infer<typeof publicStaffSchema>;
export type OtpRequest = z.infer<typeof otpRequestSchema>;
export type OtpVerify = z.infer<typeof otpVerifySchema>;
export type PublicBooking = z.infer<typeof publicBookingSchema>;
